// Banka POS Kartları — paylaşılan UI bileşenleriyle yeniden tasarlandı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const CARD_TYPE_LABELS = {
  all: 'Tümü',
  credit: 'Kredi Kartı',
  debit: 'Banka Kartı (Debit)',
  premium: 'Premium Kart',
  foreign: 'Yabancı Kart',
};

const emptyPos = {
  pos_name: '', bank_name: '', bank_account_id: '',
  merchant_id: '', terminal_id: '', notes: '', is_active: true, branch_id: '',
};

// Varsayılan komisyon şeması — tek çekim + 2-12 taksit, kart tipi 'all'
const DEFAULT_INSTALLMENT_ROWS = [
  { installment_count: 1, commission_rate: 1.79, blocking_days: 1, card_type: 'all' }, // tek çekim
  { installment_count: 2, commission_rate: 2.49, blocking_days: 2, card_type: 'all' },
  { installment_count: 3, commission_rate: 2.69, blocking_days: 3, card_type: 'all' },
  { installment_count: 4, commission_rate: 2.79, blocking_days: 4, card_type: 'all' },
  { installment_count: 5, commission_rate: 2.89, blocking_days: 5, card_type: 'all' },
  { installment_count: 6, commission_rate: 2.99, blocking_days: 6, card_type: 'all' },
  { installment_count: 7, commission_rate: 3.09, blocking_days: 7, card_type: 'all' },
  { installment_count: 8, commission_rate: 3.19, blocking_days: 8, card_type: 'all' },
  { installment_count: 9, commission_rate: 3.19, blocking_days: 9, card_type: 'all' },
  { installment_count: 10, commission_rate: 3.29, blocking_days: 10, card_type: 'all' },
  { installment_count: 11, commission_rate: 3.29, blocking_days: 11, card_type: 'all' },
  { installment_count: 12, commission_rate: 3.39, blocking_days: 12, card_type: 'all' },
];

export default function BankPosPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ bank_name: '', bank_account_id: '', is_active: '' });

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyPos });
  const [commissions, setCommissions] = useState([...DEFAULT_INSTALLMENT_ROWS]);
  const [activeTab, setActiveTab] = useState('general');
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Hareketler tab'ı için
  const [slips, setSlips] = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(false);
  const [slipFilter, setSlipFilter] = useState('all'); // all | pending | collected

  const fetchSlips = useCallback(async (posId) => {
    if (!posId) return;
    setSlipsLoading(true);
    try {
      const res = await api.get('/pos-slips', { params: { pos_id: posId } });
      if (res.data.success) setSlips(res.data.data.items || []);
    } catch (err) { console.error(err); }
    finally { setSlipsLoading(false); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/bank-pos', { params });
      if (res.data.success) setList(res.data.data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filters]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const res = await api.get('/bank-accounts');
      if (res.data.success) setBankAccounts((res.data.data.items || []).filter(b => b.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchBankAccounts(); }, [fetchBankAccounts]);
  useEffect(() => {
    api.get('/branches', { params: { is_active: 'yes' } })
      .then(r => { if (r.data?.success) setBranches(r.data.data.items || []); })
      .catch(() => {});
  }, []);

  const openDetail = async (pos) => {
    try {
      const res = await api.get('/bank-pos/' + pos.id);
      if (res.data.success) {
        const p = res.data.data;
        setSelected(p);
        setForm({
          pos_name: p.pos_name || '',
          bank_name: p.bank_name || '',
          bank_account_id: p.bank_account_id || '',
          merchant_id: p.merchant_id || '',
          terminal_id: p.terminal_id || '',
          notes: p.notes || '',
          is_active: p.is_active === true || p.is_active === 1,
          branch_id: p.branch_id || '',
        });
        const comms = p.commissions && p.commissions.length > 0 ? p.commissions : [...DEFAULT_INSTALLMENT_ROWS];
        setCommissions(comms.map(c => ({
          installment_count: c.installment_count,
          commission_rate: c.commission_rate,
          blocking_days: c.blocking_days,
          card_type: c.card_type || 'all',
        })));
        setIsNew(false); setIsEditing(false); setActiveTab('general'); setMsg(null);
        setShowModal(true);
      }
    } catch (err) { console.error(err); }
  };

  const openNew = () => {
    setSelected(null);
    setForm({ ...emptyPos });
    setCommissions([...DEFAULT_INSTALLMENT_ROWS]);
    setIsNew(true); setIsEditing(true); setActiveTab('general'); setMsg(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false); setSelected(null);
    setIsNew(false); setIsEditing(false); setMsg(null);
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const setComm = (idx, key, val) => {
    setCommissions(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], [key]: val };
      return n;
    });
  };

  const removeComm = (idx) => {
    setCommissions(prev => prev.filter((_, i) => i !== idx));
  };

  const addComm = () => {
    setCommissions(prev => [...prev, { installment_count: 1, commission_rate: 0, blocking_days: 1, card_type: 'all' }]);
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.pos_name || !form.bank_name) { setMsg({ type: 'error', text: 'POS adı ve banka adı zorunludur' }); return; }
    setSaving(true); setMsg(null);
    try {
      let id = selected?.id;
      if (isNew) {
        const r = await api.post('/bank-pos', form);
        id = r.data.data.id;
      } else {
        await api.put('/bank-pos/' + id, form);
      }
      // Komisyonları kaydet
      await api.put('/bank-pos/' + id + '/commissions', { commissions });
      setMsg({ type: 'success', text: 'Kaydedildi' });
      setIsNew(false); setIsEditing(false);
      fetchList();
      // Detayı tekrar yükle
      const r2 = await api.get('/bank-pos/' + id);
      if (r2.data.success) {
        setSelected(r2.data.data);
        setForm({
          pos_name: r2.data.data.pos_name,
          bank_name: r2.data.data.bank_name,
          bank_account_id: r2.data.data.bank_account_id || '',
          merchant_id: r2.data.data.merchant_id || '',
          terminal_id: r2.data.data.terminal_id || '',
          notes: r2.data.data.notes || '',
          is_active: r2.data.data.is_active === true || r2.data.data.is_active === 1,
        });
      }
      if (mode === 'close') setTimeout(() => closeModal(), 250);
      else if (mode === 'new') setTimeout(() => { setSelected(null); setForm({ pos_name: '', bank_name: '', is_active: true }); setIsEditing(true); setIsNew(true); setMsg(null); }, 250);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm(`"${selected.pos_name}" POS'unu silmek istiyor musunuz?`)) return;
    try {
      await api.delete('/bank-pos/' + selected.id);
      closeModal();
      fetchList();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEdit = isEditing;
  const inView = !isEditing && !isNew;

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Banka POS Kartları"
        count={list.length}
        unit="POS"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="POS adı, banka, merchant ID..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={openNew}>+ Yeni POS</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'bank_name', type: 'select', label: 'Banka', flex: '1 1 200px',
            options: [{ value: '', label: 'Tüm Bankalar' }, ...[...new Set(list.map(p => p.bank_name).filter(Boolean))].sort().map(b => ({ value: b, label: b }))] },
          { key: 'bank_account_id', type: 'select', label: 'Banka Hesabı', flex: '1 1 240px', minWidth: 200,
            options: [{ value: '', label: 'Tüm Hesaplar' }, ...bankAccounts.map(a => ({ value: a.id, label: `${a.account_name} — ${a.bank_name} (${a.currency_code})` }))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="bank-pos-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="POS bulunamadı"
          data={list}
          rowKey="id"
          onRowClick={p => openDetail(p)}
          columns={[
            { id: 'pos_name', header: 'POS Adı', accessor: 'pos_name', sortable: true,
              cell: p => <strong>{p.pos_name}</strong> },
            { id: 'bank_name', header: 'Banka', accessor: 'bank_name', sortable: true, width: 150 },
            { id: 'bank_account', header: 'Bağlı Banka Hesabı', accessor: 'bank_account_name', sortable: true, width: 200,
              cell: p => <span className="stock-secondary">{p.bank_account_name ? `${p.bank_account_name} (${p.bank_currency || 'TRY'})` : '—'}</span> },
            { id: 'merchant_id', header: 'Merchant ID', accessor: 'merchant_id', sortable: true, width: 130,
              cell: p => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.merchant_id || '—'}</span> },
            { id: 'terminal_id', header: 'Terminal ID', accessor: 'terminal_id', sortable: true, width: 130,
              cell: p => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.terminal_id || '—'}</span> },
            { id: 'status', header: 'Durum', accessor: p => p.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: p => <span className={'stock-badge ' + (p.is_active ? 'active' : 'passive')}>{p.is_active ? 'Aktif' : 'Pasif'}</span> },
          ]}
        />
      </div>

      {/* DETAY MODAL */}
      {showModal && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni POS' : 'POS Kartı'}
          size="xl"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && <Button variant="ghost" onClick={() => { if (isNew) closeModal(); else { setIsEditing(false); openDetail(selected); } }}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setIsEditing(true); setMsg(null); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {!isNew && (
            <CardHero
              icon="💳"
              code={form.pos_name}
              title={form.bank_name || 'POS'}
              subtitle={[form.merchant_id && ('MID: ' + form.merchant_id), form.terminal_id && ('TID: ' + form.terminal_id)].filter(Boolean).join(' · ')}
              statusBadge={<CardStatusBadge preset={form.is_active === false ? 'inactive' : 'active'} />}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <CardTabs
            items={[
              { key: 'general',     label: 'Genel',             icon: <TabIcon name="general"/> },
              { key: 'commissions', label: 'Komisyon Oranları', icon: <TabIcon name="pricing"/> },
              ...(isNew ? [] : [{ key: 'movements', label: 'Hareketler', icon: <TabIcon name="activity"/> }]),
              { key: 'notes',       label: 'Notlar',            icon: <TabIcon name="notes"/> },
            ]}
            active={activeTab}
            onChange={(k) => {
              setActiveTab(k);
              if (k === 'movements' && selected?.id) fetchSlips(selected.id);
            }}
          />

          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <FormField label="POS Adı" required>
                <input value={form.pos_name} onChange={e => f('pos_name', e.target.value)} disabled={!isEditing} placeholder="Örn: Garanti POS - Mağaza 1" />
              </FormField>
              <FormField label="Banka Adı" required>
                <input value={form.bank_name} onChange={e => f('bank_name', e.target.value)} disabled={!isEditing} placeholder="Örn: Garanti BBVA" />
              </FormField>
              <FormField label="Bağlı Banka Hesabı" hint="POS tahsilatlarının aktarılacağı hesap">
                <select value={form.bank_account_id} onChange={e => f('bank_account_id', e.target.value)} disabled={!isEditing}>
                  <option value="">Seçiniz</option>
                  {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name} ({b.currency_code})</option>)}
                </select>
              </FormField>
              <FormField label="Merchant ID (Üye İşyeri No)">
                <input value={form.merchant_id} onChange={e => f('merchant_id', e.target.value)} disabled={!isEditing} placeholder="Opsiyonel" />
              </FormField>
              <FormField label="Terminal ID">
                <input value={form.terminal_id} onChange={e => f('terminal_id', e.target.value)} disabled={!isEditing} placeholder="Opsiyonel" />
              </FormField>
              <FormField label="Bağlı Şube">
                <select value={form.branch_id || ''} onChange={e => f('branch_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">— Seçiniz —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.code ? b.code + ' - ' : ''}{b.name}</option>)}
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active ? '1' : '0'} onChange={e => f('is_active', e.target.value === '1')} disabled={!isEditing}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'commissions' && (
            <div>
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <strong>Komisyon Tanımları</strong> — Her taksit sayısı için ayrı bir satır. <strong>Tek çekim</strong> için taksit sayısı = <code>1</code>.
                Komisyon yüzde (%) cinsinden, blokaj iş günü olarak girilir.
                BSMV (%5) komisyon üzerinden ayrıca hesaplanır.
              </div>
              <div className="commission-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
                <table className="commission-table" style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: '0.85rem', WebkitTextSizeAdjust: '100%' }}>
                  <thead style={{ background: 'var(--bg-secondary)' }}>
                    <tr>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '0.85rem' }}>Kart Tipi</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', width: 110, fontSize: '0.85rem' }}>Taksit Sayısı</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', width: 140, fontSize: '0.85rem' }}>Komisyon (%)</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', width: 110, fontSize: '0.85rem' }}>Blokaj (gün)</th>
                      {isEditing && <th style={{ width: 50 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                        <td style={{ padding: '4px 8px' }}>
                          <select value={c.card_type || 'all'} onChange={e => setComm(i, 'card_type', e.target.value)} disabled={!isEditing}
                            style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem' }}>
                            {Object.entries(CARD_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          <input type="number" min="1" max="36" value={c.installment_count}
                            onChange={e => setComm(i, 'installment_count', e.target.value)} disabled={!isEditing}
                            style={{ width: 80, padding: '4px 8px', textAlign: 'center' }} />
                          {c.installment_count == 1 && <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginLeft: 6 }}>(Tek çekim)</span>}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                          <input type="number" step="0.01" min="0" value={c.commission_rate}
                            onChange={e => setComm(i, 'commission_rate', e.target.value)} disabled={!isEditing}
                            style={{ width: 100, padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }} />
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          <input type="number" min="0" value={c.blocking_days}
                            onChange={e => setComm(i, 'blocking_days', e.target.value)} disabled={!isEditing}
                            style={{ width: 80, padding: '4px 8px', textAlign: 'center' }} />
                        </td>
                        {isEditing && (
                          <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                            <button onClick={() => removeComm(i)}
                              style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem' }}>✕</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {isEditing && (
                <div style={{ marginTop: 10 }}>
                  <Button variant="ghost" size="sm" onClick={addComm}>+ Satır Ekle</Button>
                </div>
              )}

              {/* Örnek komisyon hesap */}
              <div style={{ marginTop: 20, padding: 12, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.8rem' }}>
                <strong>Örnek:</strong> 1000 TRY tutarında 6 taksitle çekim yapılırsa
                {(() => {
                  const row = commissions.find(c => parseInt(c.installment_count) === 6 && c.card_type === 'all');
                  if (!row) return <span> — 6 taksit için satır tanımlı değil</span>;
                  const rate = parseFloat(row.commission_rate) || 0;
                  const kom = 1000 * rate / 100;
                  const bsmv = kom * 0.05;
                  const toplam = kom + bsmv;
                  return <span> — Komisyon: <strong>{kom.toFixed(2)} TRY</strong> + BSMV: <strong>{bsmv.toFixed(2)} TRY</strong> = <strong style={{ color: 'var(--danger)' }}>{toplam.toFixed(2)} TRY</strong> kesinti, net yatacak: <strong style={{ color: 'var(--success)' }}>{(1000 - toplam).toFixed(2)} TRY</strong></span>;
                })()}
              </div>
            </div>
          )}

          {activeTab === 'movements' && (
            <div>
              {/* Filtre + özet */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'inline-flex', border: '1px solid var(--border-secondary)', borderRadius: 6, overflow: 'hidden' }}>
                  {[
                    { key: 'all',       label: 'Tümü' },
                    { key: 'pending',   label: 'Bekleyen' },
                    { key: 'collected', label: 'Tahsil Edilen' },
                  ].map(o => (
                    <button key={o.key}
                      onClick={() => setSlipFilter(o.key)}
                      style={{
                        padding: '5px 12px', fontSize: '0.82rem', cursor: 'pointer',
                        border: 'none',
                        background: slipFilter === o.key ? 'var(--accent)' : 'transparent',
                        color: slipFilter === o.key ? '#fff' : 'var(--text-primary)',
                      }}>{o.label}</button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={() => fetchSlips(selected.id)}>↻ Yenile</Button>
                <span style={{ marginLeft: 'auto', color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>
                  {slips.length} slip
                </span>
              </div>

              {(() => {
                const filtered = slips.filter((s) => slipFilter === 'all' || s.status === slipFilter);
                const sum = filtered.reduce((acc, s) => {
                  const amt = parseFloat(s.amount) || 0;
                  const com = parseFloat(s.commission_amount) || 0;
                  const bsmv = parseFloat(s.bsmv_amount) || 0;
                  const net = parseFloat(s.net_amount) || 0;
                  acc.amount += amt; acc.commission += com; acc.bsmv += bsmv; acc.net += net;
                  return acc;
                }, { amount: 0, commission: 0, bsmv: 0, net: 0 });

                return (
                  <>
                    {/* Özet kartları */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
                      {[
                        { label: 'Toplam Tutar',     value: sum.amount,     color: 'var(--text-primary)' },
                        { label: 'Toplam Komisyon',  value: sum.commission, color: 'var(--danger)' },
                        { label: 'Toplam BSMV',      value: sum.bsmv,       color: 'var(--danger)' },
                        { label: 'Net (Banka Yatan)', value: sum.net,        color: 'var(--success)' },
                      ].map((card, i) => (
                        <div key={i} style={{ padding: 10, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border-secondary)' }}>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: 3 }}>{card.label}</div>
                          <div style={{ fontSize: '1rem', fontWeight: 600, color: card.color, fontFamily: 'monospace' }}>
                            {fmtAmount(card.value)} TL
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tablo */}
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
                      <table style={{ width: '100%', minWidth: 1100, borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                        <thead style={{ background: 'var(--bg-secondary)' }}>
                          <tr>
                            <th style={th}>Tarih</th>
                            <th style={th}>Slip No</th>
                            <th style={th}>Cari</th>
                            <th style={{ ...th, textAlign: 'center' }}>Kart / Taksit</th>
                            <th style={{ ...th, textAlign: 'right' }}>Tutar</th>
                            <th style={{ ...th, textAlign: 'right' }}>Komisyon</th>
                            <th style={{ ...th, textAlign: 'right' }}>BSMV</th>
                            <th style={{ ...th, textAlign: 'right' }}>Net</th>
                            <th style={{ ...th, textAlign: 'center' }}>Durum</th>
                            <th style={th}>Tahsil / Banka</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slipsLoading && <tr><td colSpan={10} style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor…</td></tr>}
                          {!slipsLoading && filtered.length === 0 && <tr><td colSpan={10} style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>Hareket bulunamadı</td></tr>}
                          {filtered.map((s) => {
                            const isCollected = s.status === 'collected';
                            const cardLabel = CARD_TYPE_LABELS[s.card_type] || s.card_type || '-';
                            const inst = parseInt(s.installment_count) || 1;
                            return (
                              <tr key={s.id} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                                <td style={td}>{s.tx_date ? new Date(s.tx_date).toLocaleDateString('tr-TR') : '-'}</td>
                                <td style={td}>{s.slip_no || '-'}</td>
                                <td style={td}>{s.account_title || '-'}</td>
                                <td style={{ ...td, textAlign: 'center' }}>
                                  <span style={{ fontSize: '0.75rem' }}>{cardLabel}</span>
                                  {' / '}
                                  <strong>{inst === 1 ? 'Tek' : inst + ' taksit'}</strong>
                                </td>
                                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>
                                  {fmtAmount(s.amount || 0)}
                                </td>
                                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: 'var(--danger)' }}>
                                  {fmtAmount(s.commission_amount || 0)}
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginLeft: 4 }}>
                                    ({parseFloat(s.commission_rate || 0).toFixed(2)}%)
                                  </span>
                                </td>
                                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: 'var(--danger)' }}>
                                  {fmtAmount(s.bsmv_amount || 0)}
                                </td>
                                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: 'var(--success)', fontWeight: 600 }}>
                                  {fmtAmount(s.net_amount || 0)}
                                </td>
                                <td style={{ ...td, textAlign: 'center' }}>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem',
                                    background: isCollected ? '#d1fae5' : '#fef3c7',
                                    color:      isCollected ? '#065f46' : '#92400e',
                                  }}>
                                    {isCollected ? 'Tahsil edildi' : (s.status === 'pending' ? 'Bekliyor' : s.status)}
                                  </span>
                                </td>
                                <td style={td}>
                                  {isCollected
                                    ? (
                                      <div style={{ fontSize: '0.78rem' }}>
                                        <div>{s.collected_date ? new Date(s.collected_date).toLocaleDateString('tr-TR') : ''}</div>
                                        <div style={{ color: 'var(--text-tertiary)' }}>{s.collected_bank_name || ''}</div>
                                      </div>
                                    )
                                    : (s.expected_collection_date
                                        ? <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>Tahmini: {new Date(s.expected_collection_date).toLocaleDateString('tr-TR')}</span>
                                        : '-')
                                  }
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {activeTab === 'notes' && (
            <FormField label="Notlar" fullWidth>
              <textarea rows="8" value={form.notes} onChange={e => f('notes', e.target.value)} disabled={!isEditing} placeholder="POS hakkında notlar..." />
            </FormField>
          )}
        </Modal>
      )}
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-secondary)' };
const td = { padding: '6px 10px', verticalAlign: 'middle' };
